import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

const getToken = () => {
  try {
    const localData = localStorage.getItem("loggedUser");
    if (localData) {
      const parsed = JSON.parse(localData);
      if (parsed.token) return parsed.token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
    }
    const sessionData = sessionStorage.getItem("user");
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      if (parsed.token) return parsed.token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const fetchWallets = createAsyncThunk(
  "wallets/fetchWallets",
  async ({ page = 1, search = "", status = "" }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(
        `${BASE_URL}/dashboard/wallets?owner_type=user&page=${page}&search=${search}${status !== "" ? `&status=${status}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchWalletTransactions = createAsyncThunk(
  "wallets/fetchWalletTransactions",
  async ({ page = 1, user_id = "", type = "", transaction_type = "", date_from = "", date_to = "" }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");

      let params = `page=${page}`;
      if (user_id) params += `&user_id=${user_id}`;
      if (type) params += `&type=${type}`;
      if (transaction_type) params += `&transaction_type=${transaction_type}`;
      if (date_from) params += `&date_from=${date_from}`;
      if (date_to) params += `&date_to=${date_to}`;

      const response = await axios.get(
        `${BASE_URL}/dashboard/wallets/transactions?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createWalletTransaction = createAsyncThunk(
  "wallets/createWalletTransaction",
  async ({ userId, type, transaction_type, amount, order_id, reference, notes, status }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/dashboard/users/${userId}/wallet/transactions`,
        { type, transaction_type, amount, order_id, reference, notes, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchUserWalletTransactions = createAsyncThunk(
  "wallets/fetchUserWalletTransactions",
  async ({ userId, page = 1 }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(
        `${BASE_URL}/dashboard/users/${userId}/wallet/transactions?page=${page}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================================
// ADMIN WALLETS
// ============================================================

export const fetchAdminWallets = createAsyncThunk(
  "wallets/fetchAdminWallets",
  async ({ page = 1, search = "" }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(
        `${BASE_URL}/dashboard/wallets?owner_type=admin&page=${page}&search=${search}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAdminWallet = createAsyncThunk(
  "wallets/fetchAdminWallet",
  async (adminId, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(
        `${BASE_URL}/dashboard/admins/${adminId}/wallet`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAdminWalletTransactions = createAsyncThunk(
  "wallets/fetchAdminWalletTransactions",
  async ({ adminId, page = 1, from = "", to = "", date = "" }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      let params = `page=${page}`;
      if (from) params += `&from=${from}`;
      if (to) params += `&to=${to}`;
      if (date) params += `&date=${date}`;
      const response = await axios.get(
        `${BASE_URL}/dashboard/admins/${adminId}/wallet/transactions?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createAdminWalletTransaction = createAsyncThunk(
  "wallets/createAdminWalletTransaction",
  async ({ adminId, type, transaction_type, amount, notes }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const formData = new FormData();
      formData.append("type", type);
      formData.append("transaction_type", transaction_type);
      formData.append("amount", amount);
      if (notes !== undefined && notes !== null) {
        formData.append("notes", notes);
      }
      const response = await axios.post(
        `${BASE_URL}/dashboard/admins/${adminId}/wallet/transactions`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchDailySupplies = createAsyncThunk(
  "wallets/fetchDailySupplies",
  async ({ adminId = "", from = "", to = "" } = {}, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      let params = [];
      if (adminId) params.push(`admin_id=${adminId}`);
      if (from) params.push(`from=${from}`);
      if (to) params.push(`to=${to}`);
      const query = params.length ? `?${params.join("&")}` : "";
      const response = await axios.get(
        `${BASE_URL}/dashboard/wallets/daily-supplies${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createDailySupply = createAsyncThunk(
  "wallets/createDailySupply",
  async ({ date, amount } = {}, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const formData = new FormData();
      if (date) formData.append("date", date);
      if (amount) formData.append("amount", amount);
      const response = await axios.post(
        `${BASE_URL}/dashboard/wallets/daily-supplies?owner_type=admin`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateDailySupplyStatus = createAsyncThunk(
  "wallets/updateDailySupplyStatus",
  async ({ supplyId, status } = {}, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.patch(
        `${BASE_URL}/dashboard/wallets/daily-supplies/${supplyId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const fetchTransfers = createAsyncThunk(
  "wallets/fetchTransfers",
  async ({ type = "", status = "", from = "", to = "", page = 1 } = {}, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      let params = `page=${page}`;
      if (type) params += `&type=${type}`;
      if (status) params += `&status=${status}`;
      if (from) params += `&from=${from}`;
      if (to) params += `&to=${to}`;
      
      const response = await axios.get(
        `${BASE_URL}/dashboard/wallets/transfers?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createTransfer = createAsyncThunk(
  "wallets/createTransfer",
  async ({ to_admin_id, amount, date }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const formData = new FormData();
      formData.append("to_admin_id", to_admin_id);
      formData.append("amount", amount);
      formData.append("date", date);

      const response = await axios.post(
        `${BASE_URL}/dashboard/wallets/transfers`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const approveTransfer = createAsyncThunk(
  "wallets/approveTransfer",
  async ({ transferId }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/dashboard/wallets/transfers/${transferId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const rejectTransfer = createAsyncThunk(
  "wallets/rejectTransfer",
  async ({ transferId }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/dashboard/wallets/transfers/${transferId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


const walletSlice = createSlice({
  name: "wallets",
  initialState: {
    wallets: [],
    walletMeta: null,
    adminWallets: [],
    adminWalletsMeta: null,
    adminTransactions: [],
    adminTransactionsMeta: null,
    transactions: [],
    transactionsMeta: null,
    dailySupplies: [],
    dailySuppliesMeta: null,
    transfers: [],
    transfersMeta: null,
    loading: false,
    actionLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearWalletState: (state) => {
      state.loading = false;
      state.actionLoading = false;
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Wallets (users)
      .addCase(fetchWallets.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchWallets.fulfilled, (state, action) => {
        state.loading = false;
        state.wallets = action.payload.data;
        state.walletMeta = action.payload.meta;
      })
      .addCase(fetchWallets.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // Fetch Wallet Transactions
      .addCase(fetchWalletTransactions.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchWalletTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.data;
        state.transactionsMeta = action.payload.meta;
      })
      .addCase(fetchWalletTransactions.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // Create User Transaction
      .addCase(createWalletTransaction.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(createWalletTransaction.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message || "تم إنشاء العملية بنجاح";
      })
      .addCase(createWalletTransaction.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })
      // Fetch User Wallet Transactions
      .addCase(fetchUserWalletTransactions.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUserWalletTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.data;
        state.transactionsMeta = action.payload.meta;
      })
      .addCase(fetchUserWalletTransactions.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // Fetch Admin Wallets
      .addCase(fetchAdminWallets.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAdminWallets.fulfilled, (state, action) => {
        state.loading = false;
        state.adminWallets = action.payload.data || [];
        state.adminWalletsMeta = action.payload.meta || null;
      })
      .addCase(fetchAdminWallets.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // Fetch Admin Wallet Transactions
      .addCase(fetchAdminWalletTransactions.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAdminWalletTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.adminTransactions = action.payload.data || [];
        state.adminTransactionsMeta = action.payload.meta || null;
      })
      .addCase(fetchAdminWalletTransactions.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // Create Admin Transaction
      .addCase(createAdminWalletTransaction.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(createAdminWalletTransaction.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message || "تم إنشاء العملية بنجاح";
      })
      .addCase(createAdminWalletTransaction.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })
      // Fetch Daily Supplies
      .addCase(fetchDailySupplies.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchDailySupplies.fulfilled, (state, action) => {
        state.loading = false;
        // 🔧 FIX: الباك بيرجع {count, data} مش {data, meta}
        // نحاول نقرأ الشكلين عشان نضمن التوافق
        state.dailySupplies = action.payload.data || [];
        state.dailySuppliesMeta = action.payload.meta || {
          total: action.payload.count ?? 0,
          current_page: 1,
          last_page: 1,
          per_page: action.payload.count ?? 0,
        };
      })
      .addCase(fetchDailySupplies.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // Create Daily Supply
      .addCase(createDailySupply.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(createDailySupply.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message || "تم تسجيل التوريد اليومي بنجاح";
      })
      .addCase(createDailySupply.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })
      // Update Daily Supply Status
      .addCase(updateDailySupplyStatus.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(updateDailySupplyStatus.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message || "تم تحديث حالة التوريد بنجاح";
      })
      .addCase(updateDailySupplyStatus.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })
      // Fetch Transfers
      .addCase(fetchTransfers.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTransfers.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = action.payload.data || [];
        state.transfersMeta = action.payload.meta || {
          total: action.payload.count ?? 0,
          current_page: 1,
          last_page: 1,
          per_page: action.payload.count ?? 0,
        };
      })
      .addCase(fetchTransfers.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // Create Transfer
      .addCase(createTransfer.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(createTransfer.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message || "تم تقديم طلب التحويل بنجاح";
      })
      .addCase(createTransfer.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })
      // Approve Transfer
      .addCase(approveTransfer.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(approveTransfer.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message || "تمت الموافقة على التحويل بنجاح";
      })
      .addCase(approveTransfer.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; })
      // Reject Transfer
      .addCase(rejectTransfer.pending, (state) => { state.actionLoading = true; state.error = null; })
      .addCase(rejectTransfer.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.success = action.payload.message || "تم رفض التحويل بنجاح";
      })
      .addCase(rejectTransfer.rejected, (state, action) => { state.actionLoading = false; state.error = action.payload; });
  },
});

export const { clearWalletState } = walletSlice.actions;
export default walletSlice.reducer;

